import { MapNumberTo, MapStringTo, Nullable, Object_Unknown } from "../../../base-types";
import { IContentHubClient } from "../../../clients/content-hub-client";
import DirtyValueCalculator from "../../../dirty-value-calculator";
import Guard from "../../../guard";
import { EntityLoadConfiguration } from "../../querying/entity-load-configuration";
import { ChildRelationBase, IChildRelation } from "../relation";
import { RelationRole } from "../relation-role";
import { IToOneRelation } from "./to-one-relation";

export interface IChildToOneParentRelation extends IChildRelation, IToOneRelation {
    /**
     * The parent id or null.
     */
    parent: Nullable<number>;

    /**
     * Gets the properties for a parent.
     *
     * If no properties are found, an empty list is returned.
     */
    getParentProperties(): MapStringTo<Object_Unknown>;
}

export class ChildToOneParentRelation extends ChildRelationBase implements IChildToOneParentRelation {
    private _parent: Nullable<number> = null;

    readonly role: RelationRole = RelationRole.Child;

    readonly isMultiValue: boolean = false;

    get isDirty(): boolean {
        if (!this.isTracking) {
            return false;
        } else {
            return this._dirtyValueCalculator.isDirty(this._parent);
        }
    }

    get parent(): Nullable<number> {
        return this._parent;
    }

    set parent(id: Nullable<number>) {
        Guard.validIdOrNull(id);
        this._parent = id;
    }

    constructor(
        name: string,
        properties: Nullable<MapNumberTo<MapStringTo<Object_Unknown>>> = null,
        client?: IContentHubClient,
        inheritsSecurity?: boolean
    ) {
        super(name, properties, client, inheritsSecurity);
    }

    getIds(): Array<number> {
        if (this._parent !== 0 && this._parent !== null) {
            return [this._parent];
        } else {
            return [];
        }
    }

    setIds(ids: Array<number>): void {
        Guard.arrayMaxOne(ids);
        if (ids.length > 0) {
            Guard.validId(ids[0]);
            this._parent = ids[0];
        } else {
            this._parent = null;
        }
    }

    clear(): void {
        this._parent = null;
    }

    getId(): Nullable<number> {
        return this._parent;
    }

    setId(id: Nullable<number>): void {
        Guard.validIdOrNull(id);
        this._parent = id;
    }

    async setIdentifierAsync(identifier: Nullable<string>): Promise<void> {
        Guard.notNullOrUndefined(this.client);
        Guard.stringNotNullOrEmpty(identifier);

        if (identifier) {
            const entity = await this.client!.entities.getAsync(identifier, EntityLoadConfiguration.Minimal);

            if (entity && entity.id) {
                this.setId(entity.id);
                return;
            }
        }

        this.setId(null);
    }

    startTracking(): void {
        if (this.isTracking) return;

        this.isTracking = true;

        this._dirtyValueCalculator = new DirtyValueCalculator();
        this._dirtyValueCalculator.setOriginalValue(this._parent);
    }

    markClean(): void {
        if (this.isTracking) {
            this._dirtyValueCalculator.setOriginalValue(this._parent);
        }
    }

    getParentProperties(): MapStringTo<Object_Unknown> {
        if (this._parent == null) {
            return {};
        }

        return this.properties[this._parent] == null ? {} : this.properties[this._parent];
    }
}
